import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { type FormEvent, useMemo, useRef, useState } from 'react';

import { FormField, Input, Select } from '@revelio/ui';

import { AlertDialog } from '../../../../shared/components/alert-dialog';
import { useOnModalOpen } from '../../../../shared/hooks/useOnModalOpen';
import { DownRightArrowIcon } from '../../../../shared/ui/icons';
import {
  findFilterDefByField,
  getFilterDefId,
} from '../../api/columns-api-helpers';
import type {
  CustomFilter,
  CustomFilterCondition,
  CustomFilterConditionType,
  FilterOperator,
  SelectionListSelectedItem,
} from '../../data-builder.store';
import type {
  CustomFilterCategory,
  CustomFilterDefinition,
  SelectionListFilterDefinition,
} from '../../datasets/deliverable-column-config';
import {
  isConditionValid,
  serializeConditionDrafts,
} from './custom-filter-condition-helpers';
import { DateFilterValueInput } from './DateFilterValueInput';
import {
  customFilterNestedFilterSelectCssVars,
  customFilterSelectCssVars,
} from './disabled-input-overrides';
import { FilterModifierSelect } from './FilterModifierSelect';
import { NestedFilterSelect } from './NestedFilterSelect/NestedFilterSelect';
import { NumericFilterValueInput } from './NumericFilterValueInput';
import { SelectionListFilterValueInput } from './SelectionListFilterValueInput';
import { StringFilterValueInput } from './StringFilterValueInput';
import {
  getOperatorSelectOptions,
  isNullaryOperator,
} from './string-filter-operators';

export type CustomFilterModalMode =
  | {
      kind: 'free';
      defs: CustomFilterDefinition[];
      categories?: CustomFilterCategory[];
    }
  | { kind: 'locked'; def: CustomFilterDefinition };

type CustomFilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: CustomFilterModalMode;
  onSubmit: (filter: CustomFilter) => void;
  existingFilter?: CustomFilter;
  existingFilterNames: string[];
};

type ConditionDraft = {
  id: string;
  type: CustomFilterConditionType | null;
  filterDefId: string | null;
  field: string | null;
  groupKey: string | null;
  operator: FilterOperator | null;
  value: string[];
  modifier: string | null;
  selectionListItems?: SelectionListSelectedItem[];
};

function createEmptyCondition(): ConditionDraft {
  return {
    id: crypto.randomUUID(),
    type: null,
    filterDefId: null,
    field: null,
    groupKey: null,
    operator: null,
    value: [],
    modifier: null,
  };
}

function createConditionFromDef(def: CustomFilterDefinition): ConditionDraft {
  return {
    id: crypto.randomUUID(),
    type: def.type,
    filterDefId: getFilterDefId(def),
    field: def.type === 'selection_list' ? def.selectionList : def.key,
    groupKey: def.groupKey ?? null,
    operator: getDefaultOperator(def),
    value: [],
    modifier: getDefaultModifier(def),
    selectionListItems: undefined,
  };
}

function generateFilterName(existingNames: string[]): string {
  let n = existingNames.length + 1;
  let name = `filter_${n}`;
  while (existingNames.includes(name)) {
    n++;
    name = `filter_${n}`;
  }
  return name;
}

function initConditions(
  existing: CustomFilter | undefined,
  availableFilters: CustomFilterDefinition[],
  lockedDef?: CustomFilterDefinition
): ConditionDraft[] {
  if (existing) {
    return existing.conditions.map((c) => {
      const def = findFilterDefByField(c.field, availableFilters, c.groupKey);
      const filterDefId = def ? getFilterDefId(def) : c.field;
      return {
        id: crypto.randomUUID(),
        type: c.type ?? def?.type ?? null,
        filterDefId,
        field: c.field,
        groupKey: c.groupKey ?? def?.groupKey ?? null,
        operator: getDefaultOperator(def, c.operator),
        value: [...c.value],
        modifier: c.modifier ?? def?.modifiers[0]?.key ?? null,
        selectionListItems: c.selectionListItems
          ? [...c.selectionListItems]
          : undefined,
      };
    });
  }
  if (lockedDef) {
    return [createConditionFromDef(lockedDef)];
  }
  return [createEmptyCondition()];
}

function getDefaultModifier(
  def: CustomFilterDefinition | undefined
): string | null {
  return def?.modifiers[0]?.key ?? null;
}

function getDefaultOperator(
  def: CustomFilterDefinition | undefined,
  operator: FilterOperator | null = null
): FilterOperator | null {
  if (def === undefined) {
    return operator;
  }

  const operators = def.operators as FilterOperator[];

  if (operator !== null && operators.includes(operator)) {
    return operator;
  }

  return operators[0] ?? null;
}

function serializeModalState(
  currentFilterName: string,
  currentConditions: ConditionDraft[],
  currentBinary: 'AND' | 'OR' | null
): string {
  return JSON.stringify({
    filterName: currentFilterName,
    binary: currentBinary,
    conditions: serializeConditionDrafts(currentConditions),
  });
}

export function CustomFilterModal({
  isOpen,
  onClose,
  mode,
  onSubmit,
  existingFilter,
  existingFilterNames,
}: CustomFilterModalProps) {
  const isLocked = mode.kind === 'locked';
  const lockedDef = mode.kind === 'locked' ? mode.def : undefined;
  const allDefs = mode.kind === 'locked' ? [mode.def] : mode.defs;
  const categories = mode.kind === 'free' ? (mode.categories ?? []) : [];

  const [filterName, setFilterName] = useState('');
  const [conditions, setConditions] = useState<ConditionDraft[]>(() => [
    createEmptyCondition(),
  ]);
  const [binary, setBinary] = useState<'AND' | 'OR' | null>(null);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const initialStateRef = useRef<string>('');

  useOnModalOpen(isOpen, () => {
    const initialFilterName =
      existingFilter?.name ?? (lockedDef ? lockedDef.name : '');
    const initialConditions = initConditions(
      existingFilter,
      allDefs,
      lockedDef
    );
    const initialBinary = existingFilter?.binary ?? null;

    setFilterName(initialFilterName);
    setConditions(initialConditions);
    setBinary(initialBinary);
    setIsConfirmCloseOpen(false);
    initialStateRef.current = serializeModalState(
      initialFilterName,
      initialConditions,
      initialBinary
    );
  });

  const autoName = () => {
    if (!existingFilter) {
      setFilterName((currentFilterName) => {
        if (currentFilterName.trim() !== '') {
          return currentFilterName;
        }

        return generateFilterName(existingFilterNames);
      });
    }
  };

  const categorizedDefIds = new Set(
    categories.flatMap((c) => c.filters.map((d) => getFilterDefId(d)))
  );
  const topLevelDefs =
    mode.kind === 'free'
      ? mode.defs.filter((d) => !categorizedDefIds.has(getFilterDefId(d)))
      : [];

  const [modalContentEl, setModalContentEl] = useState<HTMLElement | null>(
    null
  );

  const defsById = useMemo(
    () => new Map(allDefs.map((def) => [getFilterDefId(def), def])),
    [allDefs]
  );

  const getFilterDef = (
    defId: string | null
  ): CustomFilterDefinition | undefined =>
    defId === null ? undefined : defsById.get(defId);

  const getOperatorsForField = (defId: string | null): FilterOperator[] =>
    getFilterDef(defId)?.operators ?? [];

  const updateCondition = (index: number, patch: Partial<ConditionDraft>) => {
    setConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...patch } : c))
    );
    autoName();
  };

  const removeCondition = (index: number) => {
    setConditions((prev) => {
      const nextConditions = prev.filter((_, i) => i !== index);
      if (nextConditions.length <= 1) {
        setBinary(null);
      }
      return nextConditions;
    });
  };

  const addCondition = () => {
    if (isLocked) return;
    setConditions((prev) => {
      if (prev.length === 1) {
        setBinary('AND');
      }
      return [...prev, createEmptyCondition()];
    });
  };

  const handleReset = () => {
    setFilterName(existingFilter?.name ?? (lockedDef ? lockedDef.name : ''));
    setConditions(initConditions(existingFilter, allDefs, lockedDef));
    setBinary(existingFilter?.binary ?? null);
  };

  const isDirty =
    serializeModalState(filterName, conditions, binary) !==
    initialStateRef.current;

  const requestClose = () => {
    if (isDirty) {
      setIsConfirmCloseOpen(true);
      return;
    }

    onClose();
  };

  const renderValueInput = (
    def: CustomFilterDefinition | undefined,
    condition: ConditionDraft,
    index: number,
    required: boolean
  ) => {
    if (isNullaryOperator(condition.operator)) {
      return null;
    }

    if (def?.type === 'selection_list') {
      return (
        <SelectionListFilterValueInput
          filterDef={def as SelectionListFilterDefinition}
          selectedItems={condition.selectionListItems ?? []}
          onChange={(items) => {
            updateCondition(index, {
              selectionListItems: items,
              value: items.map((i) => i.id),
            });
          }}
          isDisabled={condition.operator === null}
          required={required}
        />
      );
    }

    if (def?.type === 'numeric') {
      return (
        <NumericFilterValueInput
          values={condition.value}
          onChange={(values) => {
            updateCondition(index, { value: values });
          }}
          operator={condition.operator}
          isDisabled={condition.operator === null}
          required={required}
        />
      );
    }

    if (def?.type === 'date') {
      return (
        <DateFilterValueInput
          values={condition.value}
          onChange={(values) => {
            updateCondition(index, { value: values });
          }}
          operator={condition.operator}
          isDisabled={condition.operator === null}
          required={required}
        />
      );
    }

    if (def?.type === 'boolean') {
      return null;
    }

    return (
      <StringFilterValueInput
        values={condition.value}
        onChange={(values) => {
          updateCondition(index, { value: values });
        }}
        operator={condition.operator}
        isDisabled={condition.operator === null}
        required={required}
      />
    );
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validConditions = conditions.filter((condition) =>
      isConditionValid(condition, getFilterDef)
    );
    if (validConditions.length === 0) return;

    const effectiveBinary = validConditions.length <= 1 ? null : binary;

    const filter: CustomFilter = {
      id: existingFilter?.id ?? crypto.randomUUID(),
      name: filterName || generateFilterName(existingFilterNames),
      conditions: validConditions.map((c): CustomFilterCondition => {
        const def = getFilterDef(c.filterDefId);
        const groupKey = c.groupKey ?? def?.groupKey ?? null;
        if (def?.type === 'selection_list' && c.selectionListItems) {
          return {
            type: def.type,
            field: c.field as string,
            groupKey,
            operator: c.operator as FilterOperator,
            value: c.selectionListItems.map((item) => item.id),
            modifier: c.modifier,
            selectionListItems: c.selectionListItems,
          };
        }
        return {
          type: def?.type ?? c.type ?? 'string',
          field: c.field as string,
          groupKey,
          operator: c.operator as FilterOperator,
          value: c.value,
          modifier: c.modifier,
        };
      }),
      binary: effectiveBinary,
    };

    onSubmit(filter);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        size="2xl"
        isCentered
        closeOnOverlayClick
      >
        <ModalOverlay />
        <ModalContent data-testid="data-builder-custom-filter-modal-content">
          <Box ref={setModalContentEl} as="form" onSubmit={handleSubmit}>
            <Box px={6} pt={4} pb={2}>
              <FormField label="Filter name">
                <Input
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="Filter name"
                  style={{ fontWeight: 600 }}
                  data-testid="custom-filter-name-input"
                />
              </FormField>
            </Box>

            <Divider />

            <ModalBody>
              <Box
                background="gray.100"
                borderColor="gray.200"
                borderWidth="1px"
                borderRadius="7px"
                pt="10px"
                pb={isLocked ? '20px' : '6px'}
                px="12px"
              >
                {conditions.map((condition, index) => {
                  const isAdded = index > 0;
                  const filterDef = getFilterDef(condition.filterDefId);
                  const operatorOptions = getOperatorSelectOptions(
                    getOperatorsForField(condition.filterDefId)
                  );

                  return (
                    <Box key={condition.id}>
                      {isAdded && (
                        <Flex my="20px" alignItems="center">
                          <Divider borderBottomWidth="2px" />
                          {index === 1 ? (
                            <Select
                              size="sm"
                              value={
                                binary
                                  ? { label: binary, value: binary }
                                  : undefined
                              }
                              onChange={(opt) => {
                                if (opt) setBinary(opt.value as 'AND' | 'OR');
                              }}
                              options={[
                                { label: 'AND', value: 'AND' as const },
                                { label: 'OR', value: 'OR' as const },
                              ]}
                              style={{
                                maxWidth: '95px',
                                margin: '0 10px',
                              }}
                              cssVars={customFilterSelectCssVars}
                              data-testid="custom-filter-binary-select"
                            />
                          ) : (
                            <Text fontSize="xs" px="10px" whiteSpace="nowrap">
                              {binary}
                            </Text>
                          )}
                          <Divider borderBottomWidth="2px" />
                        </Flex>
                      )}

                      <Flex justify="space-between" alignItems="center">
                        <Box w="180px">
                          <NestedFilterSelect
                            value={condition.filterDefId}
                            selectedLabel={
                              getFilterDef(condition.filterDefId)?.name ?? ''
                            }
                            onChange={(value) => {
                              const def = getFilterDef(value);
                              const fieldValue =
                                def?.type === 'selection_list'
                                  ? def.selectionList
                                  : (def?.key ?? value);
                              updateCondition(index, {
                                type: def?.type ?? null,
                                filterDefId: value,
                                field: fieldValue,
                                groupKey: def?.groupKey ?? null,
                                operator: getDefaultOperator(def),
                                value: [],
                                modifier: getDefaultModifier(def),
                                selectionListItems: undefined,
                              });
                            }}
                            placeholder="Field"
                            isDisabled={isLocked}
                            portalContainer={modalContentEl}
                            cssVars={customFilterNestedFilterSelectCssVars}
                            data-testid={`custom-filter-field-select-${index}`}
                          >
                            {isLocked && lockedDef ? (
                              <NestedFilterSelect.Item
                                value={getFilterDefId(lockedDef)}
                                label={lockedDef.name}
                              />
                            ) : (
                              <>
                                {topLevelDefs.map((def) => (
                                  <NestedFilterSelect.Item
                                    key={getFilterDefId(def)}
                                    value={getFilterDefId(def)}
                                    label={def.name}
                                  />
                                ))}
                                {categories.map((cat) => (
                                  <NestedFilterSelect.Group
                                    key={cat.key}
                                    label={cat.name}
                                  >
                                    {cat.filters.map((def) => (
                                      <NestedFilterSelect.Item
                                        key={getFilterDefId(def)}
                                        value={getFilterDefId(def)}
                                        label={def.name}
                                      />
                                    ))}
                                  </NestedFilterSelect.Group>
                                ))}
                              </>
                            )}
                          </NestedFilterSelect>
                        </Box>

                        {isAdded && (
                          <IconButton
                            bg="none"
                            aria-label="delete condition"
                            icon={<CloseIcon boxSize={2.5} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => removeCondition(index)}
                          />
                        )}
                      </Flex>

                      <Flex mt="10px" alignItems="flex-start">
                        <DownRightArrowIcon mr="8px" mt="7px" ml="13px" />
                        <Box
                          w="143px"
                          flexShrink={0}
                          data-testid={`custom-filter-operator-select-${index}`}
                        >
                          <Select
                            size="sm"
                            value={
                              condition.operator
                                ? operatorOptions.find(
                                    (o) => o.value === condition.operator
                                  )
                                : null
                            }
                            onChange={(opt) => {
                              updateCondition(index, {
                                operator:
                                  (opt?.value as FilterOperator) ?? null,
                              });
                            }}
                            options={operatorOptions}
                            isDisabled={condition.filterDefId === null}
                            placeholder="Operator"
                            cssVars={customFilterSelectCssVars}
                            required={condition.filterDefId !== null}
                          />
                        </Box>

                        <Box flex={1} ml={2}>
                          {renderValueInput(filterDef, condition, index, true)}
                        </Box>
                      </Flex>

                      {filterDef !== undefined &&
                        filterDef.modifiers.length > 0 && (
                          <Flex mt="10px" alignItems="center">
                            <Box w="188px" flexShrink={0} />
                            <Box width="100%">
                              <FilterModifierSelect
                                modifiers={filterDef.modifiers}
                                value={condition.modifier}
                                onChange={(modifier) => {
                                  updateCondition(index, { modifier });
                                }}
                                isDisabled={condition.operator === null}
                                testId={`custom-filter-modifier-select-${index}`}
                              />
                            </Box>
                          </Flex>
                        )}
                    </Box>
                  );
                })}

                {!isLocked && (
                  <Box width="100%" textAlign="right">
                    <Button
                      colorScheme="blue"
                      fontSize="xs"
                      fontWeight="600"
                      size="sm"
                      variant="link"
                      onClick={addCondition}
                      data-testid="custom-filter-add-condition-button"
                    >
                      {binary ? `+ ${binary}` : 'AND / OR'}
                    </Button>
                  </Box>
                )}
              </Box>
            </ModalBody>

            <ModalFooter justifyContent="space-between">
              <Button
                variant="link"
                colorScheme="red"
                size="sm"
                onClick={handleReset}
              >
                Reset
              </Button>
              <Flex gap={3}>
                <Button variant="ghost" size="sm" onClick={requestClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="green"
                  size="sm"
                  type="submit"
                  data-testid="custom-filter-submit-button"
                >
                  {existingFilter ? 'Save Filter' : 'Create Filter'}
                </Button>
              </Flex>
            </ModalFooter>
          </Box>
        </ModalContent>
      </Modal>

      {isConfirmCloseOpen ? (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          onClose={() => {
            setIsConfirmCloseOpen(false);
          }}
          headerText="Discard Changes?"
          bodyContent={
            <span>
              You have unsaved changes. Do you want to continue without saving
              them?
            </span>
          }
          footerAcceptIsDestructive
          footerAcceptText="Discard"
          onAccept={() => {
            setIsConfirmCloseOpen(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
